import { useState } from 'react';
import Card from '../common/Card';
import UpgradePlanAction from './UpgradePlanAction';
import UpgradePlanModal from './UpgradePlanModal';
import { useUpgradeOptions } from '../../hooks/useBilling';
import { upgradeDeepLinkNotice, useUpgradeDeepLink } from '../../hooks/useUpgradeDeepLink';
import type { UpgradeOption } from '../../types';

export default function OtherBillingCard() {
  const [upgradeOption, setUpgradeOption] = useState<UpgradeOption | null>(null);
  const { data: upgradeOptions } = useUpgradeOptions();
  const upgradeDeepLinkStatus = useUpgradeDeepLink(upgradeOptions, setUpgradeOption);
  const deepLinkNotice = upgradeDeepLinkNotice(upgradeDeepLinkStatus);

  const upgradable = upgradeOptions?.subscriptions.filter(
    (o) => o.eligible_plans.length > 0 || o.pending_request
  ) ?? [];

  return (
    <Card className="p-6">
      {deepLinkNotice && (
        <p className={`text-sm mb-4 ${deepLinkNotice.tone === 'error' ? 'text-red-700' : 'text-amber-700'}`}>
          {deepLinkNotice.message}
        </p>
      )}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Billing Information
          </h2>
          <p className="text-gray-600">
            Your billing is managed separately. Please contact our support team
            for any billing-related questions.
          </p>
        </div>

        <div className="flex-shrink-0">
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      </div>

      {/* Plan upgrades (request flow) */}
      {upgradable.length > 0 && (
        <div className="border border-gray-200 rounded-md p-4 mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Plan Upgrades
          </h3>
          <div className="space-y-2">
            {upgradable.map((option) => (
              <div
                key={option.subscription_id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-900">
                  {option.current_plan}
                  <span className="text-gray-500 ml-2">({option.hostname})</span>
                </span>
                <UpgradePlanAction
                  option={option}
                  onUpgradeClick={setUpgradeOption}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info box about adding subscriptions */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-900">
              Want to add another subscription?
            </p>
            <p className="text-sm text-blue-800 mt-1">
              Please contact our support team to add additional subscriptions to
              your account.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <p className="text-sm text-gray-600 mb-3">
          Need help with billing? Our team is happy to assist you.
        </p>
        <a
          href="mailto:support@cloudunifi.com"
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          Contact Support
        </a>
      </div>

      {upgradeOption && (
        <UpgradePlanModal
          isOpen
          onClose={() => setUpgradeOption(null)}
          option={upgradeOption}
        />
      )}
    </Card>
  );
}
